import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import * as api from "./api";
import type { Variant } from "./types";

export const finAronKeys = {
  overview: (variant: Variant) => ["fin-aron", "overview", variant] as const,
  person: (userId: string, variant: Variant) =>
    ["fin-aron", "person", userId, variant] as const,
  overhead: (year?: number, month?: number) =>
    ["fin-aron", "overhead", year ?? "now", month ?? "now"] as const,
  comparisons: (from?: string, to?: string) =>
    ["fin-aron", "comparisons", from ?? "", to ?? ""] as const,
  suggestions: (scope: string, designerId?: string) =>
    ["fin-aron", "suggestions", scope, designerId ?? ""] as const,
  runs: (scope: string, designerId?: string) =>
    ["fin-aron", "runs", scope, designerId ?? ""] as const,
  directives: ["fin-aron", "directives"] as const,
};

export function useFinAronOverview(variant: Variant) {
  return useQuery({
    queryKey: finAronKeys.overview(variant),
    queryFn: () => api.getOverview(variant),
  });
}

export function useFinAronPerson(userId: string, variant: Variant) {
  return useQuery({
    queryKey: finAronKeys.person(userId, variant),
    queryFn: () => api.getPersonDetail(userId, variant),
    enabled: !!userId,
  });
}

export function useOverheadConfig(year?: number, month?: number) {
  return useQuery({
    queryKey: finAronKeys.overhead(year, month),
    queryFn: () => api.getOverheadConfig(year, month),
  });
}

export function useUpdateOverheadConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.putOverheadConfig,
    onSuccess: () => {
      // narzut wpływa na wszystkie marże — unieważnij cały moduł
      qc.invalidateQueries({ queryKey: ["fin-aron"] });
    },
  });
}

export function useFinAronCashflow(variant: Variant, designerId?: string) {
  return useQuery({
    queryKey: ["fin-aron", "cashflow", variant, designerId ?? ""] as const,
    queryFn: () => api.getCashflow(variant, designerId),
  });
}

export function useComparisons(from?: string, to?: string) {
  return useQuery({
    queryKey: finAronKeys.comparisons(from, to),
    queryFn: () => api.getComparisons(from, to),
  });
}

export function useSuggestions(scope: "portfolio" | "designer", designerId?: string) {
  return useQuery({
    queryKey: finAronKeys.suggestions(scope, designerId),
    queryFn: () => api.getSuggestions(scope, designerId),
  });
}

export function useRuns(scope: "portfolio" | "designer", designerId?: string) {
  return useQuery({
    queryKey: finAronKeys.runs(scope, designerId),
    queryFn: () => api.getRuns(scope, designerId),
  });
}

export function useTriggerRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ scope, designerId }: { scope: "portfolio" | "designer"; designerId?: string }) =>
      api.triggerRun(scope, designerId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["fin-aron", "suggestions"] });
      qc.invalidateQueries({ queryKey: ["fin-aron", "runs"] });
    },
  });
}

export function useSuggestionFeedback() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: "done" | "dismissed" }) =>
      api.suggestionFeedback(id, status),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["fin-aron", "suggestions"] }),
  });
}

export function useSuggestionComment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: string }) => api.suggestionComment(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["fin-aron", "suggestions"] }),
  });
}

export function useSuggestionPromote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.suggestionPromote(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["fin-aron", "suggestions"] }),
  });
}

export function useHeadToHead(a: string | null, b: string | null, variant: Variant) {
  return useQuery({
    queryKey: ["fin-aron", "h2h", a ?? "", b ?? "", variant] as const,
    queryFn: () => api.getHeadToHead(a!, b!, variant),
    enabled: !!a && !!b && a !== b,
  });
}

export function useZeShares(year: number, month: number) {
  return useQuery({
    queryKey: ["fin-aron", "ze-shares", year, month] as const,
    queryFn: () => api.getZeShares(year, month),
  });
}

export function useUpdateProjectStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.putProjectStatus,
    onSuccess: () => {
      // status projektowy wpływa na pulę wspólną narzutu — unieważnij moduł
      qc.invalidateQueries({ queryKey: ["fin-aron"] });
    },
  });
}

export function useUpdateZeShare() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.putZeShare,
    onSuccess: () => {
      // udział ZE wpływa na bazę narzutu i marże — unieważnij cały moduł
      qc.invalidateQueries({ queryKey: ["fin-aron"] });
    },
  });
}

export function useDirectives() {
  return useQuery({ queryKey: finAronKeys.directives, queryFn: api.getDirectives });
}

export function useUpdateDirectives() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.putDirectives,
    onSuccess: () => qc.invalidateQueries({ queryKey: finAronKeys.directives }),
  });
}
